import { ButtonHTMLAttributes, forwardRef } from 'react';
import { cn } from './cn';

type Variant = 'primary' | 'outline' | 'ghost' | 'danger';
type Size = 'sm' | 'md';

const variants: Record<Variant, string> = {
  primary: 'bg-brand text-black hover:bg-brand-hover disabled:opacity-45',
  outline: 'border border-line text-fg hover:border-line hover:bg-layer-strong disabled:opacity-45',
  ghost: 'text-fg-soft hover:bg-layer-strong disabled:opacity-45',
  danger: 'border border-danger/40 text-danger hover:bg-danger/10 disabled:opacity-45',
};

// `md` (default) keeps a comfortable 48px touch target (WCAG/Material) for standalone buttons;
// `sm` matches the 36px header selectors, for toolbar rows (e.g. the filter bar).
const sizes: Record<Size, string> = {
  sm: 'min-h-[36px] px-3.5',
  md: 'min-h-[48px] px-4',
};

type Props = ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: Size };

export const Button = forwardRef<HTMLButtonElement, Props>(({ variant = 'outline', size = 'md', className, ...props }, ref) => (
  <button
    ref={ref}
    className={cn(
      'inline-flex items-center justify-center gap-2 rounded-full text-[13px] font-semibold transition-colors disabled:cursor-not-allowed',
      sizes[size],
      variants[variant],
      className,
    )}
    {...props}
  />
));
Button.displayName = 'Button';

export default Button;
